"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import useEmblaCarousel, { type UseEmblaCarouselType } from "embla-carousel-react";
import { cva } from "class-variance-authority";
import { Icon } from "../Icon";
const contentVariants = cva("flex", {
  variants: {
    orientation: {
      horizontal: "-ml-4",
      vertical: "-mt-4 flex-col"
    }
  },
  defaultVariants: {
    orientation: "horizontal"
  }
});
const itemVariants = cva("min-w-0 shrink-0 grow-0", {
  variants: {
    orientation: {
      horizontal: "pl-4",
      vertical: "pt-4"
    }
  },
  defaultVariants: {
    orientation: "horizontal"
  }
});
const buttonVariants = cva("size-11 lg:size-13 xl:size-15 2xl:size-17 rounded-full outline-offset-0 focus-visible:outline-2 focus-visible:outline-offset-2 outline-yellow", {
  variants: {
    orientation: {
      horizontal: "top-1/2 -translate-y-1/2",
      vertical: "left-1/2 rotate-90"
    },
    side: {
      prev: "enabled:hover:translate-x-[-0.3rem] transition-transform duration-300",
      next: "enabled:hover:translate-x-[0.3rem] transition-transform duration-300"
    }
  },
  compoundVariants: [{
    orientation: "horizontal",
    side: "prev",
    className: "-left-12"
  }, {
    orientation: "horizontal",
    side: "next",
    className: "-right-12"
  }, {
    orientation: "vertical",
    side: "prev",
    className: "-top-12"
  }, {
    orientation: "vertical",
    side: "next",
    className: "-bottom-12"
  }]
});
type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];
type CarouselWrapperProps = {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  orientation?: "horizontal" | "vertical";
  setApi?: (api: CarouselApi) => void;
  overflowVisible?: boolean;
  variant?: "light" | "dark";
};
type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
  overflowVisible?: boolean;
} & CarouselWrapperProps;
const CarouselContext = React.createContext<CarouselContextProps | null>(null);
function useCarousel() {
  const context = React.useContext(CarouselContext);
  if (!context) {
    throw new Error("useCarousel must be used within a <Carousel />");
  }
  return context;
}
function CarouselWrapper(t0) {
  const $ = _c(47);
  let children;
  let className;
  let opts;
  let plugins;
  let props;
  let setApi;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      orientation: t1,
      opts,
      setApi,
      plugins,
      className,
      overflowVisible: t2,
      variant: t3,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = opts;
    $[4] = plugins;
    $[5] = props;
    $[6] = setApi;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    children = $[1];
    className = $[2];
    opts = $[3];
    plugins = $[4];
    props = $[5];
    setApi = $[6];
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
  }
  const orientation = t1 === undefined ? "horizontal" : t1;
  const overflowVisible = t2 === undefined ? false : t2;
  const variant = t3 === undefined ? "dark" : t3;
  const t4 = orientation === "horizontal" ? "x" : "y";
  let t5;
  if ($[10] !== opts || $[11] !== t4) {
    t5 = {
      ...opts,
      axis: t4
    };
    $[10] = opts;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const [carouselRef, api] = useEmblaCarousel(t5, plugins);
  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = api_0 => {
      if (!api_0) {
        return;
      }
      setCanScrollPrev(api_0.canScrollPrev());
      setCanScrollNext(api_0.canScrollNext());
    };
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const onSelect = t6;
  let t7;
  if ($[14] !== api) {
    t7 = () => {
      api?.scrollPrev();
    };
    $[14] = api;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const scrollPrev = t7;
  let t8;
  if ($[16] !== api) {
    t8 = () => {
      api?.scrollNext();
    };
    $[16] = api;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const scrollNext = t8;
  let t9;
  if ($[18] !== scrollNext || $[19] !== scrollPrev) {
    t9 = event => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        scrollPrev();
      } else {
        if (event.key === "ArrowRight") {
          event.preventDefault();
          scrollNext();
        }
      }
    };
    $[18] = scrollNext;
    $[19] = scrollPrev;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const handleKeyDown = t9;
  let t10;
  let t11;
  if ($[21] !== api || $[22] !== setApi) {
    t10 = () => {
      if (!api || !setApi) {
        return;
      }
      requestAnimationFrame(() => {
        setApi(api);
      });
    };
    t11 = [api, setApi];
    $[21] = api;
    $[22] = setApi;
    $[23] = t10;
    $[24] = t11;
  } else {
    t10 = $[23];
    t11 = $[24];
  }
  React.useEffect(t10, t11);
  let t12;
  let t13;
  if ($[25] !== api) {
    t12 = () => {
      if (!api) {
        return;
      }
      requestAnimationFrame(() => {
        onSelect(api);
      });
      api.on("reInit", onSelect);
      api.on("select", onSelect);
      return () => {
        api?.off("select", onSelect);
      };
    };
    t13 = [api, onSelect];
    $[25] = api;
    $[26] = t12;
    $[27] = t13;
  } else {
    t12 = $[26];
    t13 = $[27];
  }
  React.useEffect(t12, t13);
  const t14 = orientation || (opts?.axis === "y" ? "vertical" : "horizontal");
  let t15;
  if ($[28] !== api || $[29] !== canScrollNext || $[30] !== canScrollPrev || $[31] !== carouselRef || $[32] !== opts || $[33] !== overflowVisible || $[34] !== scrollNext || $[35] !== scrollPrev || $[36] !== t14 || $[37] !== variant) {
    t15 = {
      carouselRef,
      api,
      opts,
      orientation: t14,
      scrollPrev,
      scrollNext,
      canScrollPrev,
      canScrollNext,
      overflowVisible,
      variant
    };
    $[28] = api;
    $[29] = canScrollNext;
    $[30] = canScrollPrev;
    $[31] = carouselRef;
    $[32] = opts;
    $[33] = overflowVisible;
    $[34] = scrollNext;
    $[35] = scrollPrev;
    $[36] = t14;
    $[37] = variant;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== children || $[40] !== className || $[41] !== handleKeyDown || $[42] !== props) {
    t16 = <div onKeyDownCapture={handleKeyDown} className={className} role="region" aria-roledescription="carousel" data-slot="carousel" {...props}>{children}</div>;
    $[39] = children;
    $[40] = className;
    $[41] = handleKeyDown;
    $[42] = props;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] !== t15 || $[45] !== t16) {
    t17 = <CarouselContext.Provider value={t15}>{t16}</CarouselContext.Provider>;
    $[44] = t15;
    $[45] = t16;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  return t17;
}
function CarouselContent(t0) {
  const $ = _c(13);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  const {
    carouselRef,
    orientation,
    overflowVisible
  } = useCarousel();
  const t1 = overflowVisible ? "overflow-visible" : "overflow-hidden";
  let t2;
  if ($[3] !== className || $[4] !== orientation) {
    t2 = contentVariants({
      orientation,
      className
    });
    $[3] = className;
    $[4] = orientation;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== props || $[7] !== t2) {
    t3 = <div className={t2} {...props} />;
    $[6] = props;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== carouselRef || $[10] !== t1 || $[11] !== t3) {
    t4 = <div ref={carouselRef} className={t1} data-slot="carousel-content">{t3}</div>;
    $[9] = carouselRef;
    $[10] = t1;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}
function CarouselItem(t0) {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  const {
    orientation
  } = useCarousel();
  let t1;
  if ($[3] !== className || $[4] !== orientation) {
    t1 = itemVariants({
      orientation,
      className
    });
    $[3] = className;
    $[4] = orientation;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== t1) {
    t2 = <div role="group" aria-roledescription="slide" data-slot="carousel-item" className={t1} {...props} />;
    $[6] = props;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}
interface CarouselButtonProps extends React.ComponentProps<"button"> {
  side: "prev" | "next";
}
function CarouselButton(t0) {
  const $ = _c(19);
  let className;
  let props;
  let side;
  if ($[0] !== t0) {
    ({
      side,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = side;
  } else {
    className = $[1];
    props = $[2];
    side = $[3];
  }
  const {
    orientation,
    scrollPrev,
    scrollNext,
    canScrollPrev,
    canScrollNext,
    variant
  } = useCarousel();
  const iconColor = variant === "light" ? "text-yellow disabled:text-yellow/50" : "text-purple disabled:text-purple/50";
  const disabled = side === "prev" ? !canScrollPrev : !canScrollNext;
  const scroll = side === "prev" ? scrollPrev : scrollNext;
  const iconName = side === "prev" ? "muuuh-arrow-small-left" : "muuuh-arrow-small-right";
  const label = side === "prev" ? "Previous slide" : "Next slide";
  const t1 = `${iconColor} cursor-pointer transition-colors ${className}`;
  let t2;
  if ($[4] !== orientation || $[5] !== side || $[6] !== t1) {
    t2 = buttonVariants({
      orientation,
      side,
      className: t1
    });
    $[4] = orientation;
    $[5] = side;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== iconName) {
    t3 = <Icon name={iconName} className="size-full" />;
    $[8] = iconName;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== label) {
    t4 = <span className="sr-only">{label}</span>;
    $[10] = label;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== disabled || $[13] !== props || $[14] !== scroll || $[15] !== t2 || $[16] !== t3 || $[17] !== t4) {
    t5 = <button disabled={disabled} onClick={scroll} className={t2} {...props}>{t3}{t4}</button>;
    $[12] = disabled;
    $[13] = props;
    $[14] = scroll;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  return t5;
}
function CarouselPrevious(t0) {
  const $ = _c(4);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  let t1;
  if ($[2] !== props) {
    t1 = <CarouselButton side="prev" {...props} />;
    $[2] = props;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function CarouselNext(t0) {
  const $ = _c(4);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  let t1;
  if ($[2] !== props) {
    t1 = <CarouselButton side="next" {...props} />;
    $[2] = props;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
export { type CarouselApi, type CarouselWrapperProps, CarouselWrapper, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext };